import React, { useCallback, useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';

import { chatPublicDomain } from '../../util/config';
import { useChatLogin } from '../../contexts/ChatLoginContext';
import { useConfiguration } from '../../contexts/ConfigurationContext';

import { PollPanel } from './Poll.styled';

interface PollProps {
	poll: any;
	pollResult?: any;
	settings?: any;
	answer?: any;
	isMobile?: boolean;
}

const Poll = (props: PollProps) => {

	const { username, userId } = useChatLogin();
	const { accountId, currentMediaId, configParams,setPollExpanded } = useConfiguration();

	const [isExpanded, setExpanded] = useState(true);
	const [selections, setSelections] = useState<any[]>([]);
	const pollIdRef = useRef(props.poll?.id);

	const isIgnoreChatCookies = useCallback(() => {
		if (configParams.ignoreChatCookies) {
			return configParams.ignoreChatCookies === 'true';
		}

		return props.settings?.additionalSettings?.noChatCookies === true;
	}, [props.settings?.additionalSettings?.noChatCookies, configParams.ignoreChatCookies]);

	const postAnswer = useCallback(
		(newSelection) => {
			if (!newSelection || newSelection.length === 0) {
				return;
			}

			const pollReplyUrl = `${chatPublicDomain}${accountId}/medias/${currentMediaId}/polls/${props.poll.id}/replies`;
			var tempObj = {
				sender: {
					userId: userId,
					name: username,
				},
				reply: { selections: newSelection },
			};

			fetch(pollReplyUrl, {
				method: 'POST',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(tempObj),
			})
				.then((res) => {
					if (res) {
						setTimeout(() => {
							setExpanded(false);
							if (!isIgnoreChatCookies()) {
								const votedPolls = Cookies.get(`chat_poll_voted_${accountId}`);
								const newVotedPolls = `${votedPolls ?? ''},${props.poll?.id}-${newSelection}`;
								Cookies.set(`chat_poll_voted_${accountId}`, newVotedPolls);
							}
						});
					}
				})
				.catch((error) => {
					// eslint-disable-next-line no-console
					console.log("Can't Vote " + error);
				});
		},
		[accountId, currentMediaId, isIgnoreChatCookies, props.poll?.id, userId, username]
	);

	const onSelectAnswer = useCallback(
		(_event, answerId) => {
			const maxSelections = props.poll?.selections;
			let newSelection: any[] = [];
			if (maxSelections === 1) {
				newSelection = [answerId];
			}

			if (selections.length < maxSelections) {
				newSelection = [...selections, answerId];
			}

			// TODO - show a notification said that not allow more than maxSelections answers
			setSelections(newSelection);
			postAnswer(newSelection);
		},
		[props.poll, selections, postAnswer]
	);

	const isVoted = useCallback(
		(id) => {
			const votedPolls = Cookies.get(`chat_poll_voted_${accountId}`);
			return (votedPolls ?? '').includes(id);
		},
		[accountId]
	);

	useEffect(() => {
		if (props.poll?.id !== pollIdRef.current) {
			setSelections([]);
			setExpanded(true);
		}
		pollIdRef.current = props.poll?.id;
	}, [props.poll?.id]);

	// Auto-expand once when the poll gets closed (to show results), but let the user collapse it afterwards
	useEffect(() => {
		if (props.poll?.closed) {
			setExpanded(true);
		}
	}, [props.poll?.closed]);

	useEffect(() => {
		setPollExpanded(isExpanded);
	}, [isExpanded]);

	if (props.poll?.published === false) {
		return null;
	}

	return (
		<PollPanel
			settings={props.settings}
			voted={props.poll?.id && isVoted(props.poll?.id)}
			isExpanded={isExpanded}
			selections={selections}
			isClosed={props.poll?.closed}
			poll={props.poll}
			pollResult={props.pollResult}
			onTogglePoll={() => setExpanded((prevState) => !prevState)}
			onSelectAnswer={onSelectAnswer}
			isMobile={props.isMobile}
		/>
	);
};

export default Poll;
